import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import * as SC from './EvolutionCombinedChartSection.styles';
import Tooltip from '../Tooltip/Tooltip';
import { defaultTheme } from '../../../../../../utils/defaultTheme';
import CombinedBarLineChart from '../../../../../../common/components/CombinedBarLineChart/CombinedBarLineChart';
import { generateTooltipDataByGroup } from '../../tabs/utils/utils';
import { createClassName } from './../../../../../../common/utils/utils';
import { useWindowSize } from '../../../../../../common/hooks/useWindowSize';

const EvolutionCombinedChartSection = props => {
    const { sectionID, legendItems, combinedChartsData, wrapperRef } = props;
    const [width] = useWindowSize();

    // Memoized function that will re-run when page width changes
    const foreignObjectXAxis = useCallback(
        rectLabel => {
            if (wrapperRef.current) {
                //set xAxisForeignObject based on the width of the first bar's rect to have text not overlapping
                const rectElement = wrapperRef.current.querySelector(`.bar-${createClassName(rectLabel)} rect`);
                if (rectElement) {
                    const rectWidth = rectElement.getAttribute('width');
                    return rectWidth && rectWidth <= 12
                        ? {
                              width: 25,
                              translateX: -14,
                              translateY: -45,
                          }
                        : null;
                }
            }
            return null;
        },
        [width, wrapperRef]
    );

    //in order to have 2 sections side by side when we have 12 bars 4 + 8 we can dinamically change the size of charts by the proportions it covers
    //case <=12 two sections side by side
    // the section width should sum up 50% of the wrapper in order to have space for two sections on same row substracting margins
    //case >12 one section per row
    // the section width should sum up 100% of the wrapper substracting margins
    const computeWidth = (isMaxNumberOfBars, numberOfBars, isSameNumberOfBars, numberOfSections) => {
        if (wrapperRef.current) {
            if (numberOfSections <= 1) {
                if (isMaxNumberOfBars) {
                    return wrapperRef.current.clientWidth * 0.6 - 80;
                } else {
                    return wrapperRef.current.clientWidth * 0.4 - 80;
                }
            }
            if (numberOfBars <= 12) {
                if (isSameNumberOfBars) return wrapperRef.current.clientWidth * 0.25 - 40;
                if (isMaxNumberOfBars) {
                    return wrapperRef.current.clientWidth * 0.3 - 40;
                } else {
                    return wrapperRef.current.clientWidth * 0.2 - 40;
                }
            } else {
                if (isSameNumberOfBars) return wrapperRef.current.clientWidth * 0.5 - 60;
                if (isMaxNumberOfBars) {
                    return wrapperRef.current.clientWidth * 0.6 - 80;
                } else {
                    return wrapperRef.current.clientWidth * 0.4 - 80;
                }
            }
        }
    };

    const combinedChartGroups = combinedChartsData[sectionID]
        .sort((a, b) => a.order - b.order)
        .reduce((acc, item) => {
            if (item.chartName.includes(item.chartTitle)) {
                // Find if a group for this chartTitle already exists add it to existant array
                let group = acc.find(arr => arr.length > 0 && arr[0].chartTitle === item.chartTitle);
                if (group) {
                    group.push(item);
                } else {
                    acc.push([item]);
                }
            }
            return acc;
        }, []);

    const marketAverageItem = legendItems.filter(item => item.key === 0);
    const legendItemsFiltered = legendItems.filter(item => item.isChecked).concat(marketAverageItem);

    return (
        <SC.EvolutionCombinedChartSectionWrapper>
            {combinedChartGroups.map((combinedChartGroup, i) => {
                const maxNumberOfBarsPerGroup = Math.max(...combinedChartGroup.map(item => item.barData.values.length));
                const numberOfBars = combinedChartGroup
                    .map(item => item.barData.values.length)
                    .reduce((a, b) => a + b, 0);
                const isSameNumberOfBars = combinedChartGroup.every(
                    item => item.barData.values.length === maxNumberOfBarsPerGroup
                );
                return (
                    <SC.SectionWrapper key={combinedChartGroup[0].chartTitle + i}>
                        <SC.ChartSectionHeaderWrapper>
                            <SC.ChartSectionTitle
                                style={{
                                    backgroundColor: combinedChartGroup[0].color ?? defaultTheme.blue[600],
                                }}
                            >
                                {combinedChartGroup[0].chartTitle}
                            </SC.ChartSectionTitle>
                            <div style={{ gridArea: 'legend', display: 'flex', justifyContent: 'flex-end' }}>
                                <SC.LegendWrapper>
                                    {legendItemsFiltered.map(legendItem => (
                                        <div key={legendItem.key}>
                                            <SC.LegendLine
                                                legendColor={legendItem.color}
                                                barWidth={legendItem.key === 0 ? '10px' : '20px'}
                                                customMargin={legendItem.key === 0 ? '0 5px 0 0' : '0 8px 0 0'}
                                            >
                                                {legendItem.key === 0 ? (
                                                    Array.from({ length: 3 }, (_, ind) => <span key={ind} />)
                                                ) : (
                                                    <span />
                                                )}
                                            </SC.LegendLine>
                                            <Tooltip tooltipText={legendItem.itemLabel}>
                                                <SC.LegendLabel title={legendItem.itemLabel}>
                                                    {legendItem.itemLabel}
                                                </SC.LegendLabel>
                                            </Tooltip>
                                        </div>
                                    ))}
                                </SC.LegendWrapper>
                            </div>
                        </SC.ChartSectionHeaderWrapper>
                        <SC.ChartSectionChartWrapper
                            data-testid={`CombinedBarLineChart-${combinedChartGroup[0].chartTitle}`}
                        >
                            {combinedChartGroup?.map((d, index) => {
                                const isMaxNumberOfBars = maxNumberOfBarsPerGroup === d.barData.values.length;
                                const visibleLOBs = legendItemsFiltered.map(item => item.itemLabel);

                                return (
                                    d.lineData.length > 0 &&
                                    Object.keys(d.barData).length > 0 && (
                                        <CombinedBarLineChart
                                            key={sectionID + d.chartTitle + d.barData.values.length}
                                            visibleLOBs={visibleLOBs}
                                            lineData={d.lineData}
                                            barData={d.barData}
                                            customWidthOnPage={_width =>
                                                computeWidth(
                                                    isMaxNumberOfBars,
                                                    numberOfBars,
                                                    isSameNumberOfBars,
                                                    combinedChartGroups.length
                                                ) ?? _width
                                            }
                                            customLineTooltip={{
                                                tooltipFontColor: defaultTheme.blue[900],
                                            }}
                                            isEvolutionReport={true}
                                            alwaysOnLineTooltip={true}
                                            showBrandName={true}
                                            tooltipKpiDescription={combinedChartGroup[0].chartTitle}
                                            topBrandsTooltipContent={generateTooltipDataByGroup(
                                                combinedChartGroup[index],
                                                visibleLOBs
                                            )}
                                            foreignObjects={foreignObjectXAxis(d.barData.values[0].group)}
                                        />
                                    )
                                );
                            })}
                        </SC.ChartSectionChartWrapper>
                    </SC.SectionWrapper>
                );
            })}
        </SC.EvolutionCombinedChartSectionWrapper>
    );
};

export default EvolutionCombinedChartSection;

EvolutionCombinedChartSection.propTypes = {
    sectionID: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    wrapperRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }).isRequired,
    legendItems: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.number.isRequired,
            itemLabel: PropTypes.string.isRequired,
            color: PropTypes.string.isRequired,
            isChecked: PropTypes.bool.isRequired,
            isDefaultChecked: PropTypes.bool.isRequired,
        })
    ).isRequired,
    combinedChartsData: PropTypes.objectOf(
        PropTypes.oneOfType([
            PropTypes.array,
            PropTypes.arrayOf(
                PropTypes.shape({
                    chartName: PropTypes.string.isRequired,
                    chartTitle: PropTypes.string.isRequired,
                    color: PropTypes.string,
                    barData: PropTypes.array.isRequired,
                    lineData: PropTypes.array.isRequired,
                })
            ),
        ])
    ).isRequired,
};
